import Link from 'next/link';
import { makeStyles } from 'tss-react/mui';
import {
  Root as DropdownMenuRoot,
  Trigger,
  Portal,
  Group,
  Label,
  Arrow,
  Item,
} from '@radix-ui/react-dropdown-menu';
import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import UserAvatarButton from '../UserAvatar/UserAvatarButton';
import { MenuContent, MenuSeparator } from '../DropdownMenu/components';
import theme from '../../theme';

const useStyles = makeStyles()(() => ({
  content: {
    // Needed to overlay menu on top of the navbar itself
    zIndex: theme.zIndex.tooltip,
    maxWidth: 'calc(18vw)',
    minWidth: '200px',
    backgroundColor: palette.gray_900,
    '& div[role="menuitem"]': {
      '&:not([data-disabled=""])': {
        backgroundColor: palette.gray_900,
        '&[data-highlighted]': {
          backgroundColor: palette.gray_700,
        },
      },
    },
  },
  arrow: {
    fill: palette.gray_900,
  },
  accountInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    padding: theme.spacing(1, 1, 0.5),
  },
  name: {
    color: palette.gray_0,
    fontSize: theme.typography.button.fontSize,
  },
  accountInfoSubLabel: {
    color: palette.gray_75,
    paddingTop: theme.spacing(0.5),
  },
  capitalize: {
    textTransform: 'capitalize',
    padding: 0,
    marginRight: '4px',
  },
  firstUppercase: {
    '&:first-letter': {
      textTransform: 'uppercase',
    },
  },
  inline: {
    whiteSpace: 'nowrap',
  },
  truncateLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingBottom: '4px',
  },
  primaryAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(1),
    borderRadius: 4,
    padding: '4px 8px',
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
    width: 'fit-content',
    '&:hover': {
      backgroundColor: palette.gray_800,
      color: theme.palette.primary.light,
    },
  },
  upgradeContainer: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(2.75),
  },
  iconMenuItem: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: palette.gray_800,
    height: theme.spacing(4.25),
  },
  customIcon: {
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
}));

export interface AccountDropdownMenuProps
  extends Omit<AccountDropdownMenuContentProps, 'open'> {
  userId: string;
  userInitials: string;
  disablePortal?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface AccountDropdownMenuContentProps {
  userName: string;
  email: string;
  subscriptionName: string;
  showUpgrade: boolean;
  onUpgrade?: () => void;
  onSignOut?: () => void;
}

const AccountDropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  AccountDropdownMenuContentProps
>((props, ref) => {
  const {
    userName,
    email,
    subscriptionName,
    showUpgrade,
    onUpgrade,
    onSignOut,
  } = props;

  const { classes, cx } = useStyles();

  const minWidthOverride = {
    minWidth: 0,
    paddingRight: 0,
  };

  return (
    <MenuContent
      ref={ref}
      id="user-profile-menu"
      className={classes.content}
      side="right"
      sideOffset={16}
      collisionPadding={8}
      aria-label={`User profile menu. ${userName} is currently signed in. ${subscriptionName} is your current subscription`}
    >
      <Group>
        <Label className={classes.accountInfoContainer}>
          <Typography
            variant="pMd"
            bold
            noWrap
            className={cx(classes.name, classes.capitalize)}
            aria-label={`${userName} is currently signed in.`}
          >
            {userName}
          </Typography>
          <Typography
            variant="pSm"
            noWrap
            className={cx(classes.accountInfoSubLabel, classes.truncateLabel)}
            aria-label={`${subscriptionName} is your email`}
          >
            {email}
          </Typography>
          <div className={classes.upgradeContainer}>
            <Typography
              variant="pSm"
              className={cx(
                classes.accountInfoSubLabel,
                classes.firstUppercase,
                classes.inline
              )}
              aria-label={`${subscriptionName} is your current subscription`}
            >{`${subscriptionName} account`}</Typography>
            {showUpgrade && (
              <Item asChild onSelect={onUpgrade}>
                <Typography
                  variant="pSm"
                  bold
                  color="action"
                  className={classes.primaryAction}
                >{`Upgrade`}</Typography>
              </Item>
            )}
          </div>
        </Label>
        <MenuSeparator />
        <Link href={'/dashboard/account'}>
          <Item className={classes.iconMenuItem}>
            <Icon type="settings01" style={minWidthOverride} />
            <Typography variant="pSm">{`Account settings`}</Typography>
          </Item>
        </Link>
        <Link
          href={`https://help.wellsaid.io/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Item className={classes.iconMenuItem}>
            <Icon type="helpCircle" style={minWidthOverride} />
            <Typography variant="pSm">{`Help center`}</Typography>
          </Item>
        </Link>
        <Link
          href={`https://help.wellsaid.io/hc/en-us/articles/39720425068051-Release-Notes`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Item className={classes.iconMenuItem}>
            <Icon type="file02" style={minWidthOverride} />
            <Typography variant="pSm">{`Release notes`}</Typography>
          </Item>
        </Link>
        <MenuSeparator />
        <Item onSelect={onSignOut} className={classes.iconMenuItem}>
          <Icon
            type="arrowCircle"
            style={{
              ...minWidthOverride,
            }}
            className={classes.customIcon}
          />
          <Typography variant="pSm">{`Sign out`}</Typography>
        </Item>
      </Group>
      <Arrow className={classes.arrow} />
    </MenuContent>
  );
});

const AccountDropdownMenu = (props: AccountDropdownMenuProps) => {
  const {
    userId,
    userInitials,
    disablePortal = false,
    onOpenChange,
    ...rest
  } = props;
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const handleTriggerClick = useCallback(() => {
    setOpen(!open);
    if (onOpenChange) onOpenChange(!open);
  }, [onOpenChange, open]);

  /**
   * Because the user profile menu's tooltip has nav links,
   * We should close the tooltip when we redirect user to those pages
   */
  useEffect(() => {
    const routeChanged = () => {
      setOpen(false);
      if (onOpenChange) onOpenChange(false);
    };

    router?.events.on('routeChangeComplete', routeChanged);

    return () => {
      router?.events.off('routeChangeComplete', routeChanged);
    };
  }, [onOpenChange, router?.events, router?.route]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (onOpenChange) onOpenChange(isOpen);
  };

  return (
    <DropdownMenuRoot open={open} onOpenChange={handleOpenChange}>
      <Trigger asChild onClick={handleTriggerClick}>
        <UserAvatarButton
          open={open}
          userId={userId}
          userInitials={userInitials}
        />
      </Trigger>
      {disablePortal && <AccountDropdownMenuContent {...rest} />}
      {!disablePortal && (
        <Portal>
          <AccountDropdownMenuContent {...rest} />
        </Portal>
      )}
    </DropdownMenuRoot>
  );
};

export default AccountDropdownMenu;
